// Internal dependencies
import { getMapBoxToken } from './map';

/**
 * Place search ("geocoding") helpers.
 *
 * With a Mapbox public key configured, places are looked up through the
 * Mapbox geocoder. Without one they are looked up through Photon, a free
 * OpenStreetMap geocoder run by komoot that needs no key and is meant for
 * search-as-you-type use. Photon results are reshaped to the Mapbox
 * ("Carmen GeoJSON") layout, so everything downstream handles one shape:
 * `center` as `[lng, lat]`, `bbox` as `[minLng, minLat, maxLng, maxLat]`,
 * `place_name` and `trTitle` for display.
 *
 * @link https://docs.mapbox.com/api/search/geocoding/
 * @link https://photon.komoot.io/
 */

export const MAPBOX_GEOCODING_URL =
  'https://api.mapbox.com/geocoding/v5/mapbox.places/';
export const PHOTON_GEOCODING_URL = 'https://photon.komoot.io/api/';

// Result types that are places rather than streets or addresses
export const MAPBOX_PLACE_TYPES = 'country,region,place,locality,neighborhood';
export const PHOTON_PLACE_LAYERS = [
  'country',
  'state',
  'county',
  'city',
  'district',
  'locality',
];

// Mapbox result types and their nearest Photon layer
const MAPBOX_TYPE_TO_PHOTON_LAYER = {
  country: 'country',
  region: 'state',
  place: 'city',
  locality: 'locality',
  neighborhood: 'district',
  address: 'street',
};

/**
 * Which geocoder is in use: `mapbox` when a public key is configured,
 * otherwise `photon`.
 */
export function getGeocoderName() {
  return getMapBoxToken() ? 'mapbox' : 'photon';
}

/**
 * Mapbox geocoding request URL
 *
 * @param {string} query
 * @param {object} options
 * @param {string} options.token - Mapbox public key
 * @param {string} [options.types] - comma separated result types
 * @param {number} [options.limit]
 */
export function getMapboxGeocodingUrl(query, { token, types, limit } = {}) {
  let url =
    MAPBOX_GEOCODING_URL +
    encodeURIComponent(query) +
    '.json?access_token=' +
    encodeURIComponent(token || '') +
    '&language=en';
  if (limit) {
    url += '&limit=' + limit;
  }
  if (types) {
    url += '&types=' + types;
  }
  return url;
}

/**
 * Photon geocoding request URL
 *
 * @param {string} query
 * @param {object} options
 * @param {string[]} [options.layers] - result layers to include
 * @param {number} [options.limit]
 */
export function getPhotonGeocodingUrl(query, { layers, limit } = {}) {
  let url = PHOTON_GEOCODING_URL + '?q=' + encodeURIComponent(query);
  url += '&lang=en';
  if (limit) {
    url += '&limit=' + limit;
  }
  (layers || []).forEach(layer => {
    url += '&layer=' + layer;
  });
  return url;
}

/**
 * Translate Mapbox result types (`country,place,...`) to Photon layers.
 * Returns `undefined` for no restriction.
 */
export function mapboxTypesToPhotonLayers(types) {
  if (!types) {
    return undefined;
  }
  const layers = types
    .split(',')
    .map(type => MAPBOX_TYPE_TO_PHOTON_LAYER[type.trim()])
    .filter(Boolean);
  return layers.length ? [...new Set(layers)] : undefined;
}

/**
 * Short display title for a Photon result, e.g. "Dublin, Leinster, Ireland".
 * Region and country are added only when they say something new.
 */
export function photonTitle(properties = {}) {
  const parts = properties.name ? [properties.name] : [];
  ['state', 'country'].forEach(key => {
    const value = properties[key];
    if (value && !parts.includes(value)) {
      parts.push(value);
    }
  });
  return parts.join(', ');
}

/**
 * Reshape a Photon feature to the Mapbox Carmen GeoJSON layout.
 * Photon's `extent` is `[minLng, maxLat, maxLng, minLat]`.
 */
export function photonFeatureToCarmen(feature) {
  const { properties = {}, geometry = {} } = feature;
  const title = photonTitle(properties);
  const result = {
    id: 'photon.' + (properties.osm_type || '') + (properties.osm_id || ''),
    text: properties.name || title,
    place_name: title,
    trTitle: title,
    center: geometry.coordinates,
    geometry,
    properties,
  };

  const { extent } = properties;
  if (Array.isArray(extent) && extent.length === 4) {
    result.bbox = [extent[0], extent[3], extent[2], extent[1]];
  }

  return result;
}

/**
 * Look up places for a query with whichever geocoder is available.
 * Resolves to Carmen-shaped results, or an empty list when the query is too
 * short or the request fails, so callers can treat lookups as best effort.
 *
 * @param {string} query
 * @param {object} options
 * @param {number} [options.limit=5]
 * @param {boolean} [options.placesOnly=true] - leave out streets and addresses
 */
export async function geocode(query, { limit = 5, placesOnly = true } = {}) {
  if (!query || query.trim().length <= 1) {
    return [];
  }

  const token = getMapBoxToken();
  const url = token
    ? getMapboxGeocodingUrl(query, {
        token,
        limit,
        types: placesOnly ? MAPBOX_PLACE_TYPES : undefined,
      })
    : getPhotonGeocodingUrl(query, {
        limit,
        layers: placesOnly ? PHOTON_PLACE_LAYERS : undefined,
      });

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return [];
    }
    const { features = [] } = await response.json();
    return token
      ? features.map(feature => ({ ...feature, trTitle: feature.place_name }))
      : features.map(photonFeatureToCarmen);
  } catch {
    return [];
  }
}
